import { useCurrentUser, useView } from '@/hooks';
import { isAdmin } from '@/types';
import { BottomBarButton, BottomBarMenuItem, SideBarButton } from '@/ui';

import type { User } from '@/types';

import { usesMomentsScheduling } from '@/features/appointments/schedulingProvider';
import {
  getCompanyAdminCompanyIds,
  hasFeature,
  isCoach,
  isCompanyAdmin,
  isSupport,
} from '@/types/user';

// Not put in utils to avoid having JSX outside of components
const buildMenu = (
  user: User,
  isClient: boolean,
  isNewCalendarsEnabled: boolean,
) => {
  const isSupportUser = isSupport(user) && !isAdmin(user);
  const shouldLimitForSupport = isSupportUser && !isClient;

  const menu = [
    <SideBarButton
      key="Home"
      icon="home"
      route={isClient ? '/m' : '/coach'}
      title="Home"
    />,
  ];

  if (isClient) {
    menu.push(
      <SideBarButton
        key="Sessions"
        activeRoutes={['/m/sessions']}
        icon="calendar_today"
        route="/m/sessions/upcoming"
        title="Sessions"
      />,
    );
  } else if (isCoach(user)) {
    if (isNewCalendarsEnabled) {
      menu.push(
        <SideBarButton
          key="Schedule"
          activeRoutes={['/coach/schedule']}
          icon="calendar_today"
          route="/coach/schedule"
          title="Schedule"
        />,
      );
    } else {
      menu.push(
        <SideBarButton
          key="Sessions"
          activeRoutes={['/coach/sessions']}
          icon="calendar_today"
          route="/coach/sessions/upcoming"
          title="Sessions"
        />,
      );
    }
  }

  // Support users only get limited navigation while in the support/coach app.
  // In the member app, show the normal member nav so they can get back to
  // their own member experience like coaches/admins can.
  if (!shouldLimitForSupport) {
    menu.push(
      <SideBarButton
        key="member-growth"
        icon="landscape"
        route="/m/growth"
        title="Growth Plan"
      />,
      <SideBarButton
        key="member-insights"
        icon="insights"
        route="/m/insights"
        title="Insights"
      />,
    );

    if (hasFeature(user, 'documents')) {
      menu.push(
        <SideBarButton
          key="member-documents"
          icon="article"
          route="/documents"
          title="Documents"
        />,
      );
    }
  }

  if (isCompanyAdmin(user)) {
    const companyIds = getCompanyAdminCompanyIds(user);
    const route =
      companyIds.length === 1 ? `/company/${companyIds[0]}` : '/company';
    menu.push(
      <SideBarButton
        key="company-admin"
        icon="domain"
        route={route}
        title="Company"
      />,
    );
  }

  if (isCoach(user)) {
    menu.push(
      <SideBarButton
        key="coach-members"
        icon="group"
        route="/coach/members"
        title="Members"
      />,
    );

    menu.push(
      <SideBarButton
        key="app-switcher"
        icon="switch_account"
        route={isClient ? '/coach' : '/m'}
        title={isClient ? 'Coach app' : 'Client app'}
      />,
    );
  }

  if (isAdmin(user)) {
    menu.push(
      <SideBarButton
        key="coach-admin"
        icon="admin_panel_settings"
        route="/coach/manage"
        title="Manage"
      />,
    );
  }

  // Support users get access to Manage (coaches and companies only), and a
  // switcher back to the member app when they are in the support/coach app.
  if (isSupportUser) {
    if (isClient) {
      menu.push(
        <SideBarButton
          key="support-manage"
          icon="admin_panel_settings"
          route="/coach/manage"
          title="Manage"
        />,
      );

      if (!isCoach(user)) {
        menu.push(
          <SideBarButton
            key="app-switcher"
            icon="switch_account"
            route="/coach"
            title="Support app"
          />,
        );
      }
    } else {
      menu.push(
        <SideBarButton
          key="support-manage"
          icon="admin_panel_settings"
          route="/coach/manage"
          title="Manage"
        />,
      );

      if (!isCoach(user)) {
        menu.push(
          <SideBarButton
            key="app-switcher"
            icon="switch_account"
            route="/m"
            title="Client app"
          />,
        );
      }
    }
  }

  return menu;
};

const buildMoreBottomMenu = (user: User, isClient: boolean) => {
  const isSupportUser = isSupport(user) && !isAdmin(user);
  const shouldLimitForSupport = isSupportUser && !isClient;
  const menu = [];

  // Support users only get limited navigation while in the support/coach app.
  if (!shouldLimitForSupport) {
    menu.push(
      <BottomBarMenuItem
        key="member-insights"
        icon="insights"
        label="Insights"
        route="/m/insights"
      />,
    );

    if (hasFeature(user, 'documents')) {
      menu.push(
        <BottomBarMenuItem
          key="member-documents"
          icon="article"
          label="Documents"
          route="/documents"
        />,
      );
    }
  }

  if (isCoach(user)) {
    menu.push(
      <BottomBarMenuItem
        key="coach-templates"
        icon="article"
        label="Templates"
        route="/coach/templates"
      />,
    );
  }

  if (isAdmin(user)) {
    menu.push(
      <BottomBarMenuItem
        key="coach-manage"
        icon="admin_panel_settings"
        label="Manage"
        route="/coach/manage"
      />,
    );
  }

  // Support users get access to Manage (coaches and companies only)
  if (isSupport(user) && !isAdmin(user)) {
    menu.push(
      <BottomBarMenuItem
        key="support-manage"
        icon="admin_panel_settings"
        label="Manage"
        route="/coach/manage"
      />,
    );
  }

  if (isCompanyAdmin(user)) {
    const companyIds = getCompanyAdminCompanyIds(user);
    const route =
      companyIds.length === 1 ? `/company/${companyIds[0]}` : '/company';
    menu.push(
      <BottomBarMenuItem
        key="company-admin"
        icon="domain"
        label="Company"
        route={route}
      />,
    );
  }

  return menu;
};

const buildBottomMenu = (
  user: User,
  isClient: boolean,
  isNewCalendarsEnabled: boolean,
) => {
  const isSupportUser = isSupport(user) && !isAdmin(user);
  const shouldLimitForSupport = isSupportUser && !isClient;
  const home = isClient ? '/m' : '/coach';

  const bottomMenu = [
    <BottomBarButton key="mobile-home" icon="home" label="Home" route={home} />,
  ];

  if (isClient || isCoach(user)) {
    let sessionsRoute = '/coach/sessions/upcoming';
    let sessionsActiveRoutes = ['/coach/sessions'];
    if (isClient) {
      sessionsRoute = '/m/sessions/upcoming';
      sessionsActiveRoutes = ['/m/sessions'];
    } else if (isNewCalendarsEnabled) {
      sessionsRoute = '/coach/schedule';
      sessionsActiveRoutes = ['/coach/schedule'];
    }

    bottomMenu.push(
      <BottomBarButton
        key="mobile-sessions"
        activeRoutes={sessionsActiveRoutes}
        icon="calendar_today"
        label={isClient || !isNewCalendarsEnabled ? 'Sessions' : 'Schedule'}
        route={sessionsRoute}
      />,
    );
  }

  if (isCoach(user)) {
    bottomMenu.push(
      <BottomBarButton
        key="coach-members"
        icon="group"
        label="Members"
        route="/coach/members"
      />,
    );
  } else if (!shouldLimitForSupport) {
    // Support users don't see Growth in bottom nav while in support/coach app
    bottomMenu.push(
      <BottomBarButton
        key="client-growth"
        icon="landscape"
        label="Growth"
        route={`${home}/growth`}
      />,
    );
  }

  return bottomMenu;
};

export const useLayout = () => {
  const { user, isUserLoading } = useCurrentUser();
  const { isClient, isViewLoading } = useView();
  if (isUserLoading || isViewLoading) {
    return {
      bottomMenu: null,
      menu: null,
      moreBottomMenu: null,
    };
  }

  const coachOnMentoCalendar = isCoach(user) && usesMomentsScheduling(user);
  const menu = buildMenu(user, isClient, coachOnMentoCalendar);
  const bottomMenu = buildBottomMenu(user, isClient, coachOnMentoCalendar);
  const moreBottomMenu = buildMoreBottomMenu(user, isClient);

  const homePath = isClient ? '/m' : '/coach';
  return { bottomMenu, homePath, menu, moreBottomMenu };
};

export default useLayout;
